import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Box } from '@material-ui/core';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { globalEventsChannel } from '../lib/events';
import { Row } from './layout/Row';
import { Column } from './layout/Column';
import { useQueryParams } from './utils/hooks';
import { useGlobalAppState } from '../context/GlobalAppStateContext';
import { SET_USER_TEAM } from '../mutation/team';

interface TeamCheckWrapperProps {
  teamId?: string
  dataTeamId?: string
}

export const InvalidTeamMessage = () => {
  const { t } = useTranslation();

  return (
    <div className="central-message">
      <Paper elevation={12}>
        <Box p={3}>
          <Row>
            <Column md={2}>
              <Box height="100%" display="flex" justifyContent="center">
                <HourglassEmptyIcon fontSize="large" />
              </Box>
            </Column>

            <Column md={10}>
              <h3>{t('global.invalidTeam.title', 'Requested data is from another team')}</h3>
              <p>{t('global.invalidTeam.text', 'Please wait, changing team selection')}</p>
            </Column>
          </Row>
        </Box>
      </Paper>
    </div>
  );
};

const TeamCheckWrapper: React.FC<TeamCheckWrapperProps & {children: JSX.Element}> = (props) => {
  const {
    children,
    dataTeamId,
    teamId,
  } = props;

  const [queryParams] = useQueryParams();
  const { teamIsChanging, setTeamIsChanging } = useGlobalAppState();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [setTeam] = useMutation(SET_USER_TEAM, {
    onError: async () => {
      setTeamIsChanging(false);
    },
    onCompleted: async () => {
      setTeamIsChanging(false);
    },
  });

  const comparedTeamId = dataTeamId || queryParams.teamId;

  const updateTeam = useCallback((tId: string) => {
    if (teamIsChanging) {
      return;
    }
    setTeamIsChanging(tId);

    enqueueSnackbar(t('teams.dialog.teamChanged', 'Data from another team. Changing team.'), {
      variant: 'warning',
      autoHideDuration: 5000,
    });
    setTeam({
      variables: {
        teamId: tId,
      },
    });
  }, [teamIsChanging]);

  useEffect(() => {
    // console.log('teamIsChanging')
    const unsubscribeTeamChange = globalEventsChannel.on('onTeamChange', (tId) => {
      if (!teamIsChanging) {
        // console.log('event - onTeamChange', tId, teamIsChanging)
        updateTeam(tId);
      } else {
        // console.log('event - onTeamChange _ TEAM IS CHANGING', tId, teamIsChanging)
      }
    });
    return () => {
      unsubscribeTeamChange();
      // setTeamIsChanging(false);
    };
  }, [teamIsChanging]);

  useEffect(() => {
    if (!teamIsChanging) {
      if (comparedTeamId && teamId && teamId !== comparedTeamId) {
        // console.log('globaleEventsEmit', comparedTeamId, teamId, teamIsChanging)
        globalEventsChannel.emit('onTeamChange', comparedTeamId);
      }
    }
  }, [comparedTeamId, teamId, teamIsChanging]);

  return comparedTeamId && teamId && teamId !== comparedTeamId
    ? <InvalidTeamMessage />
    : children;
};

export default TeamCheckWrapper;
